import { useEffect, useRef } from 'react';
import { authService } from '../services/auth.service';

/**
 * Hook for proactive token refresh
 * Automatically refreshes tokens before they expire
 */
export const useTokenRefresh = () => {
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastRefreshRef = useRef<number>(0);

  // Function to check and refresh token if needed
  const checkAndRefreshToken = async () => {
    try {
      const tokenStatus = authService.getTokenStatus();
      
      if (!tokenStatus.hasAccessToken || !tokenStatus.hasRefreshToken) {
        return;
      }

      if (tokenStatus.refreshTokenExpired) {
        console.warn('Refresh token expired, cannot refresh access token');
        return;
      }

      if (tokenStatus.needsRefresh) {
        const now = Date.now();
        const timeSinceLastRefresh = now - lastRefreshRef.current;
        
        // Prevent refreshing too frequently (minimum 30 seconds between refreshes)
        if (timeSinceLastRefresh < 30000) {
          return;
        }

        await authService.refreshToken();
        lastRefreshRef.current = now;
      }
    } catch (error) {
      console.error('Proactive token refresh failed:', error);
    }
  };

  // Set up periodic token refresh check
  useEffect(() => {
    // Check immediately on mount
    checkAndRefreshToken();

    // Set up interval to check every minute
    refreshIntervalRef.current = setInterval(checkAndRefreshToken, 60000);

    // Cleanup on unmount
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, []);

  // Function to manually trigger token refresh
  const manualRefresh = async () => {
    try {
      await authService.refreshToken();
      lastRefreshRef.current = Date.now();
      return true;
    } catch (error) {
      console.error('Manual token refresh failed:', error);
      return false;
    }
  };

  // Function to get current token status
  const getTokenStatus = () => {
    return authService.getTokenStatus();
  };

  return {
    checkAndRefreshToken,
    manualRefresh,
    getTokenStatus
  };
}; 